/**
 * Defines the hover and crosshair operations chart components consume, plus an
 * in-memory implementation for hosts without application-managed shared state.
 *
 * Application hosts adapt their existing highlight store through
 * PanelRunsLinePlot/graphInteraction.ts. Keeping that translation separate lets
 * this contract and its consumers work without application highlight keys,
 * setters, or constants. The adapter owns those dependencies; this module owns
 * the chart-facing operations and local state behavior.
 *
 * Both implementations expose readers and event writers for the same state.
 * Consumers subscribe where they draw a value instead of passing changing
 * pointer snapshots through Graph. Selective subscriptions control rerenders;
 * the module split keeps the renderer independent of application storage.
 */
import {useSyncExternalStore} from 'react';

/** A highlighted identity can apply across every metric in a panel. */
export const ALL_METRICS = Symbol('all-metrics');
export type GraphMetric = string | typeof ALL_METRICS;

export type GraphInteractionSource = {
  getHighlightedId: () => string | undefined;
  getHighlightedMetric: () => string | symbol | undefined;
  /** Axis keys and x values use the chart's displayed label and coordinate units. */
  getX: (axis: string) => number | undefined;
  subscribe: (onChange: () => void) => () => void;
};

export type GraphPointerMove = {
  axis: string;
  x: number;
  /** Omission keeps the existing highlight when no nearest point exists. */
  highlight?: {id: string | undefined; metric: GraphMetric | undefined};
};

/** Source and event writers must address the same logical identities and units. */
export type GraphInteraction = {
  source: GraphInteractionSource;
  events: {
    onSeriesHover: (
      id: string | undefined,
      metric: GraphMetric | undefined
    ) => void;
    onPointerMove: (event: GraphPointerMove) => void;
    onPointerLeave: (axis: string) => void;
  };
};

function subscribeNever() {
  return () => {};
}

/**
 * Reads one primitive value from either a local or application-backed source.
 * Store notifications rerender this consumer only when its snapshot changes:
 * an x-only update need not rerender a consumer reading the highlighted series.
 * Offscreen consumers return undefined without reading or subscribing.
 */
export function useInteractionValue<
  T extends string | number | symbol | undefined,
>(
  source: GraphInteractionSource,
  read: () => T,
  onScreen: boolean
): T | undefined {
  function getSnapshot() {
    return onScreen ? read() : undefined;
  }
  return useSyncExternalStore(
    onScreen ? source.subscribe : subscribeNever,
    getSnapshot
  );
}

/**
 * Creates independent interaction state without an application store.
 * Graph uses one instance per mount when the interaction prop is omitted. A host
 * can retain and share one instance to coordinate several charts; separate
 * instances keep their highlights and pointer positions independent.
 */
export function createLocalGraphInteraction(): GraphInteraction {
  let highlightedId: string | undefined;
  let highlightedMetric: GraphMetric | undefined;
  const positions = new Map<string, number>();
  const listeners = new Set<() => void>();
  function publish(changed: boolean) {
    if (changed) {
      listeners.forEach(listener => listener());
    }
  }
  function setHighlight(
    id: string | undefined,
    metric: GraphMetric | undefined
  ) {
    const nextMetric = id == null ? undefined : metric;
    const changed = id !== highlightedId || nextMetric !== highlightedMetric;
    highlightedId = id;
    highlightedMetric = nextMetric;
    return changed;
  }
  return {
    source: {
      getHighlightedId: () => highlightedId,
      getHighlightedMetric: () => highlightedMetric,
      getX: axis => positions.get(axis),
      subscribe(onChange) {
        listeners.add(onChange);
        return () => {
          listeners.delete(onChange);
        };
      },
    },
    events: {
      onSeriesHover(id, metric) {
        publish(setHighlight(id, metric));
      },
      onPointerMove({axis, x, highlight}) {
        const moved = positions.get(axis) !== x;
        positions.set(axis, x);
        const changed =
          highlight == null
            ? false
            : setHighlight(highlight.id, highlight.metric);
        publish(moved || changed);
      },
      onPointerLeave(axis) {
        const moved = positions.delete(axis);
        const changed = setHighlight(undefined, undefined);
        publish(moved || changed);
      },
    },
  };
}
